import { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaThLarge, FaTable, FaSpinner, FaSearch, FaTimes } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import ClientCard from '../components/ClientCard';
import ClientTable from '../components/ClientTable';
import ClientModal from '../components/ClientModal';
import ClientForm from '../components/ClientForm';
import DocumentsModal from '../components/DocumentsModal';
import Toast from '../components/Toast';
import ConfirmModal from '../components/ConfirmModal';
import Pagination from '../components/Pagination';
import { clientesAPI } from '../services/api';

const Dashboard = () => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('viewMode') || 'cards';
  });

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalClientes, setTotalClientes] = useState(0);
  const perPage = 10;

  // Busca
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Modais
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [clienteToEdit, setClienteToEdit] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [clienteToDelete, setClienteToDelete] = useState(null);

  // Toast
  const [toast, setToast] = useState(null);

  useEffect(() => {
    carregarClientes();
  }, [currentPage, searchQuery]);

  useEffect(() => {
    localStorage.setItem('viewMode', viewMode);
  }, [viewMode]);

  const carregarClientes = async () => {
    try {
      setLoading(true);
      const { data } = await clientesAPI.listar(currentPage, perPage, searchQuery);
      setClientes(data.clientes);
      setTotalPages(data.pages);
      setTotalClientes(data.total);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      setToast({ message: 'Erro ao carregar clientes', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCardClick = (cliente) => {
    setSelectedCliente(cliente);
    setShowClientModal(true);
  };

  const handleVerDocumentos = (cliente) => {
    setSelectedCliente(cliente);
    setShowClientModal(false);
    setShowDocumentsModal(true);
  };

  const handleNovoCliente = () => {
    setClienteToEdit(null);
    setShowClientForm(true);
  };

  const handleEditar = (cliente) => {
    setClienteToEdit(cliente);
    setShowClientForm(true);
  };

  const handleClientFormSuccess = async (message, clienteParaEditar = null) => {
    if (clienteParaEditar) {
      // Abrir formulário em modo de edição com o cliente que tem CPF duplicado
      setClienteToEdit(clienteParaEditar);
      setShowClientForm(true);
    } else {
      // Sucesso normal
      setShowClientForm(false);
      setClienteToEdit(null);
      await carregarClientes();
      if (message) {
        setToast({ message, type: 'success' });
      }
    }
  };

  const handleExcluir = (cliente) => {
    setClienteToDelete(cliente);
    setShowConfirmDelete(true);
  };

  const confirmDelete = async () => {
    if (!clienteToDelete) return;

    try {
      await clientesAPI.deletar(clienteToDelete.id);
      await carregarClientes();
      setToast({ message: 'Cliente excluído com sucesso!', type: 'success' });
    } catch (error) {
      console.error('Erro ao excluir cliente:', error);
      setToast({ message: 'Erro ao excluir cliente', type: 'error' });
    } finally {
      setShowConfirmDelete(false);
      setClienteToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirmDelete(false);
    setClienteToDelete(null);
  };

  const toggleViewMode = () => {
    setViewMode((prev) => (prev === 'cards' ? 'table' : 'cards'));
  };

  // Debounce para busca
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== searchQuery) {
        setSearchQuery(searchTerm);
        setCurrentPage(1); // Reset para página 1 ao buscar
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Clientes</h1>
            <p className="text-gray-600 mt-1">
              {searchQuery ? (
                <>
                  {totalClientes} {totalClientes === 1 ? 'resultado encontrado' : 'resultados encontrados'}
                  {searchQuery && <span className="ml-1">para "{searchQuery}"</span>}
                  {totalClientes > perPage && (
                    <span className="ml-2 text-sm">
                      (Página {currentPage} de {totalPages})
                    </span>
                  )}
                </>
              ) : (
                <>
                  {totalClientes} {totalClientes === 1 ? 'cliente cadastrado' : 'clientes cadastrados'}
                  {totalClientes > perPage && (
                    <span className="ml-2 text-sm">
                      (Página {currentPage} de {totalPages})
                    </span>
                  )}
                </>
              )}
            </p>
          </div>

          <div className="flex items-center space-x-4">
            {/* Search Input */}
            <div className="relative">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Pesquisar por nome ou CPF/CNPJ..."
                  className="pl-10 pr-10 py-2 w-80 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            </div>

            {/* Toggle View */}
            <button
              onClick={toggleViewMode}
              className="flex items-center space-x-2 bg-white hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg border border-gray-300 transition duration-200"
            >
              {viewMode === 'cards' ? (
                <>
                  <FaTable />
                  <span>Visualização em Tabela</span>
                </>
              ) : (
                <>
                  <FaThLarge />
                  <span>Visualização em Cards</span>
                </>
              )}
            </button>

            {/* Novo Cliente */}
            <button
              onClick={handleNovoCliente}
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition duration-200 shadow-lg"
            >
              <FaPlus />
              <span>Novo Cliente</span>
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <FaSpinner className="animate-spin text-blue-600 text-5xl mb-4" />
            <p className="text-gray-600 text-lg">Carregando clientes...</p>
          </div>
        ) : clientes.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="text-gray-400 mb-4">
              <FaPlus className="text-6xl mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Nenhum cliente cadastrado
            </h3>
            <p className="text-gray-500 mb-6">
              Comece adicionando seu primeiro cliente
            </p>
            <button
              onClick={handleNovoCliente}
              className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition duration-200"
            >
              <FaPlus />
              <span>Adicionar Primeiro Cliente</span>
            </button>
          </div>
        ) : viewMode === 'cards' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clientes.map((cliente) => (
              <ClientCard
                key={cliente.id}
                cliente={cliente}
                onClick={handleCardClick}
                onVerDocumentos={handleVerDocumentos}
                onEditar={handleEditar}
                onExcluir={handleExcluir}
              />
            ))}
          </div>
        ) : (
          <ClientTable
            clientes={clientes}
            onVerDocumentos={handleVerDocumentos}
            onEditar={handleEditar}
            onExcluir={handleExcluir}
          />
        )}

        {/* Paginação */}
        {!loading && clientes.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      {/* Modais */}
      {showClientModal && (
        <ClientModal
          cliente={selectedCliente}
          onClose={() => {
            setShowClientModal(false);
            setSelectedCliente(null);
          }}
          onVerDocumentos={handleVerDocumentos}
          onEditar={handleEditar}
        />
      )}

      {showClientForm && (
        <ClientForm
          clienteEdit={clienteToEdit}
          onClose={() => {
            setShowClientForm(false);
            setClienteToEdit(null);
          }}
          onSuccess={handleClientFormSuccess}
        />
      )}

      {showDocumentsModal && (
        <DocumentsModal
          cliente={selectedCliente}
          onClose={() => {
            setShowDocumentsModal(false);
            setSelectedCliente(null);
          }}
        />
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showConfirmDelete && clienteToDelete && (
        <ConfirmModal
          title="Excluir Cliente"
          message={`Tem certeza que deseja excluir o cliente "${clienteToDelete.nome}"? Esta ação não pode ser desfeita e todos os documentos associados também serão excluídos.`}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          confirmText="Excluir"
          cancelText="Cancelar"
        />
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;
